import { computed, reactive, ref, watch } from "vue";
import { query } from "gql-query-builder";
import { sortBy } from "lodash";
import HttpClient from "project-blue-http-client";
import { GQL_QUERIES } from "../utils/APIs";
import { SUBSCRIPTION_INFO } from "./subscription";


export const SELECTED_ELEMENT_TO_MAP = ref("*");
export const PROFILE_MARKS = ref([]);
export const MAP_ELEMENTS = ref([]);
export const inProgress = ref(false);
export const reqSignal = ref(new AbortController());
export const MAP_ELEMENT_OPTS = ref([]);

export const getMapData = async (el) => {
  const gqlQuery = GQL_QUERIES.GET_MAP_DATA;
  reqSignal.value.abort();
  reqSignal.value = new AbortController();
  inProgress.value = true;


  let client = new HttpClient(gqlQuery.baseUrl);
  client.get(`${gqlQuery.controller}/${gqlQuery.operation}/${el}`)
  .then(response => {
    PROFILE_MARKS.value = response.marks;
    MAP_ELEMENTS.value = response.elements;
    inProgress.value = false;
  });

};

/**
 * Fetches map elements from the elements controller API
 * Expected API response structure:
 * {
 *   elements: [
 *     { name: "Element Name", code: "EL" }
 *   ]
 * }
 */
export const getMapElements = async () => {
  try {
    // Try the API endpoint first since we now have a working endpoint
    const gqlQuery = GQL_QUERIES.GET_MAP_ELEMENTS;

    let client = new HttpClient(gqlQuery.baseUrl);
    const endpoint = `${gqlQuery.controller}/${gqlQuery.operation}`;

    const response = await client.get(endpoint);

    // Check if response is an array (direct response) or has elements property (wrapped response)
    let elementsArray = null;
    if (Array.isArray(response)) {
      // Direct array response from API
      elementsArray = response;
    } else if (response && response.elements && Array.isArray(response.elements)) {
      // Wrapped response with elements property
      elementsArray = response.elements;
    }

    if (elementsArray && elementsArray.length > 0) {
      // Transform the API response to match the expected format
      const transformedElements = elementsArray.map(element => ({
        label: element.name || 'Unknown',
        value: element.code || element.name
      })).filter(element => element.label && element.value); // Filter out invalid entries

      // Add the "Critical Materials" option at the beginning
      MAP_ELEMENT_OPTS.value = [
        { label: "Critical Materials", value: "*" },
        ...transformedElements
      ];

      return;
    }

    // Fallback to subscription data if API doesn't return valid data
    if (SUBSCRIPTION_INFO.value?.elements && SUBSCRIPTION_INFO.value.elements.length > 0) {
      // Transform the subscription elements to match the expected format
      const transformedElements = SUBSCRIPTION_INFO.value.elements.map(element => ({
        label: element.name || element.code || 'Unknown',
        value: element.code || element.name
      })).filter(element => element.label && element.value);

      // Add the "Critical Materials" option at the beginning
      MAP_ELEMENT_OPTS.value = [
        { label: "Critical Materials", value: "*" },
        ...transformedElements
      ];

      return;
    }

    // Final fallback to static data if both API and subscription fail
    MAP_ELEMENT_OPTS.value = [
      { label: "Critical Materials", value: "*" },
      { label: "Chromium", value: "Cr" },
      { label: "Cobalt", value: "Co" },
      { label: "Fluorspar", value: "F" },
      { label: "Gallium", value: "Ga" },
      { label: "Graphite", value: "Graphite" },
      { label: "Iron", value: "Fe" },
      { label: "Lithium", value: "Li" },
      { label: "Manganese", value: "Mn" },
      { label: "Molybdenum", value: "Mo" },
      { label: "Nickel", value: "Ni" },
      { label: "Niobium", value: "Nb" },
      { label: "Scandium", value: "Sc" },
      { label: "Vanadium", value: "V" },
      { label: "Silicon", value: "Si" },
      { label: "REE", value: "REE" },
      { label: "Salt", value: "SALT" },
      { label: "Magnesium", value: "Mg" },
      { label: "Tantalum", value: "Ta" },
      { label: "Tin", value: "Sn" },
      { label: "Titanium", value: "Ti" },
      { label: "Tungsten", value: "W" }
    ];
  } catch (error) {
    // Try subscription data as fallback if API fails
    if (SUBSCRIPTION_INFO.value?.elements && SUBSCRIPTION_INFO.value.elements.length > 0) {
      const transformedElements = SUBSCRIPTION_INFO.value.elements.map(element => ({
        label: element.name || element.code || 'Unknown',
        value: element.code || element.name
      })).filter(element => element.label && element.value);

      MAP_ELEMENT_OPTS.value = [
        { label: "Critical Materials", value: "*" },
        ...transformedElements
      ];

      return;
    }

    // Final fallback to static data if everything fails
    MAP_ELEMENT_OPTS.value = [
      { label: "Critical Materials", value: "*" },
      { label: "Chromium", value: "Cr" },
      { label: "Cobalt", value: "Co" },
      { label: "Fluorspar", value: "F" },
      { label: "Gallium", value: "Ga" },
      { label: "Graphite", value: "Graphite" },
      { label: "Iron", value: "Fe" },
      { label: "Lithium", value: "Li" },
      { label: "Manganese", value: "Mn" },
      { label: "Molybdenum", value: "Mo" },
      { label: "Nickel", value: "Ni" },
      { label: "Niobium", value: "Nb" },
      { label: "Scandium", value: "Sc" },
      { label: "Vanadium", value: "V" },
      { label: "Silicon", value: "Si" },
      { label: "REE", value: "REE" },
      { label: "Salt", value: "SALT" },
      { label: "Magnesium", value: "Mg" },
      { label: "Tantalum", value: "Ta" },
      { label: "Tin", value: "Sn" },
      { label: "Titanium", value: "Ti" },
      { label: "Tungsten", value: "W" }
    ];
  }
};

watch(SELECTED_ELEMENT_TO_MAP, async (element) => {
  await getMapData(element);
});
